/**
 * The app pages of a server (what `/` shows): signing in, then your teams and their spaces; an
 * invite on top when the page was opened from one. Opening a space leaves for the editor
 * (`/s/<space>`), whose top bar leads back here.
 *
 * Coming back from Google or GitHub, the address carries a one-time ticket (`?auth=`), traded
 * here for the session; then the page goes on to the space it was turned away from, if any.
 */
import { Match, Show, Switch, onMount, type JSX } from "solid-js";
import { toast } from "../shared/toast";
import { Toasts } from "../shared/ui";
import { usePages } from "./context";
import { AccountPage } from "./AccountPage";
import { Dashboard } from "./Dashboard";
import { JoinCard } from "./JoinCard";
import { nextPath, signInResult, withoutReturns } from "./routes";
import { SignInCard } from "./SignInCard";
import { TopBar } from "./TopBar";

/** The whole page. */
export function Pages(): JSX.Element {
  const { accounts, server, route } = usePages();
  /** Whether this browser has a session on this server. */
  const signedIn = () => accounts.sessions().some((s) => s.server === server);
  onMount(async () => {
    const result = signInResult(location.search);
    if (!result) return;
    const next = nextPath(location.search);
    history.replaceState(null, "", `${location.pathname}${withoutReturns(location.search)}`);
    if ("error" in result) {
      toast(`Signing in failed: ${result.error}`, "error");
      return;
    }
    if ((await accounts.finishSignIn(server, result.ticket)) && next) {
      const params = new URLSearchParams(withoutReturns(location.search));
      params.delete("next");
      location.assign(`${next}${params.size ? `?${params}` : ""}`);
    }
  });
  return (
    <div class="pages" id="pages" data-page={route().page} data-signed-in={signedIn() ? "yes" : "no"}>
      <TopBar />
      <main class="pages-main">
        <Switch>
          <Match when={route().page === "join"}>
            <JoinCard />
            <Show when={!signedIn()}>
              <SignInCard creating />
            </Show>
          </Match>
          <Match when={!signedIn()}>
            <SignInCard />
          </Match>
          <Match when={route().page === "account" && accounts.me(server)}>
            <AccountPage />
          </Match>
          <Match when={accounts.me(server)}>
            <Dashboard />
          </Match>
          <Match when={true}>
            <p class="muted pages-loading">Loading your teams…</p>
          </Match>
        </Switch>
      </main>
      <Toasts />
    </div>
  );
}
